<template>
  <div class="datepicker">
    <div class="datepicker__input">
      <Input
        v-model="internalValue"
        ref="input"
        maxlength="10"
        :label="label"
        :placeholder="placeholder"
        :secondary="secondary"
        :borderDashed="borderDashed"
        :borderSolid="borderSolid"
        :borderBottom="borderBottom"
        :boxShadow="boxShadow"
        :round="round"
        :disabled="disabled"
        @click="onFocusInput"
        @focus="onFocusInput"
        @blur="onBlurInput"
      />
    </div>
    <transition name="datepicker--fade">
      <div class="datepicker__picker" v-if="datepickerIsOpen">
        <DatepickerPlugin
          v-model="date"
          :inline="true"
          :language="language"
          :format="dateFormat"
          :disabled="disabled"
          :disabled-dates="disabledDates"
          @selected="onSelectedDate"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import * as moment from 'moment';
import Datepicker from 'vuejs-datepicker';
import * as lang from "vuejs-datepicker/src/locale";
import Input from "./Input";
const IDisabledDates = {
  to: Date,
  from: Date,
  days: Array,
  daysOfMonth: Array,
  dates: Array,
  ranges: Array,
  customPredictor: Function,
};
const IHighlightedDates = IDisabledDates;
export default {
  name: "Datepicker",
  status: "prototype",
  release: "1.0.0",
  components: { DatepickerPlugin: Datepicker, Input },
  props: {
    value: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    borderBottom: {
      type: Boolean,
      default: false,
    },
    borderDashed: {
      type: Boolean,
      default: false,
    },
    borderSolid: {
      type: Boolean,
      default: false,
    },
    boxShadow: {
      type: Boolean,
      default: false,
    },
    round: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledDates: {
      type: IDisabledDates,
      default: () => {},
    },
    highlighted: {
      type: IHighlightedDates,
      default: () => {},
    },
  },
  data() {
    return {
      language: lang['ptBR'],
      date: "",
      lazyValue: this.value,
      datepickerIsOpen: false,
    }
  },
  mounted() {
    if (!this.$refs.input || !this.$refs.input.$el) {
      return;
    }
    const input = this.$refs.input.$el.getElementsByTagName('input')[0];
    const formatDate = (val) => {
      return val
        .replace(/\D/, '')
        .replace(/^(\d{2})(\d+)/, '$1/$2')
        .replace(/^(\d{2})\/(\d{2})(\d+)/, '$1/$2/$3');
    };
    const keypressHandler = (e) => {
      if (!e || !e.srcElement) {
        return;
      }

      e.srcElement.value = formatDate(e.srcElement.value)
    };
    input.addEventListener('keyup', keypressHandler);
    input.addEventListener('keydown', keypressHandler);
  },
  computed: {
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        this.date = moment(val,'DD/MM/YYYY').format();
        this.lazyValue = val;
        this.$emit("input", val);
      }
    },
  },
  watch: {
    value(val) {
      this.date = moment(val,'DD/MM/YYYY').format();
      this.lazyValue = val;
    },
  },
  methods: {
    onFocusInput(e) {
      if (!this.disabled) {
        this.datepickerIsOpen = true;
      }
    },
    onBlurInput(e) {
      this.datepickerIsOpen = false;
    },
    onSelectedDate(date) {
      this.internalValue = moment(date).format('DD/MM/YYYY');
      this.datepickerIsOpen = false;
    },
    dateFormat(date) {
      return moment(date).format('DD/MM/YYYY');
    }
  },
}
</script>

<style lang="scss">
@import "../../styles/styles.scss";
.datepicker {
  &--fade-enter-active,
  &--fade-leave-active {
    transition: opacity .2s ease;
  }
  &--fade-enter,
  &--fade-leave-to {
    opacity: 0;
  }
  font-family: $font-text;
  &__picker {
    position: absolute;
    z-index: 999;
  }
  .vdp-datepicker__calendar {
    box-shadow: $shadow-l;
    border: 0;
    color: $color-purple-darker;
    padding: 5px;
    border-radius: 5px;
    background: $color-white;
    overflow: hidden;
    header {
      .prev {
        border-radius: 5px;
        &:after {
          border-right-color: $color-purple-darker;
        }
        &:not(.disabled):hover {
          background: transparent;
          &:after {
            border-right-color: $color-purple-lighter;
          }
        }
      }
      .next {
        border-radius: 5px;
        &:after {
          border-left-color: $color-purple-darker;
        }
        &:not(.disabled):hover {
          background: transparent;
          &:after {
            border-left-color:$color-purple-lighter;
          }
        }
      }
      .up {
        border-radius: 5px;
        &:not(.disabled):hover {
          background: transparent;
          color: $color-purple-lighter;
        }
      }
    }
    .cell {
      border: 0;
      border-radius: 5px;
      &.selected {
        color: $color-white;
        background: $color-purple-darker;
      }
      &:not(.blank):not(.disabled) {
        &.day:hover,
        &.month:hover,
        &.year:hover {
          border: 0;
          color: $color-purple-lighter;
          background: transparent;
        }
      }
    }
  }
}
</style>
